(ns kyber.events.authentication
  (:require [aws.cognito.errors :as errors]
            [clojure.string :as str]
            [cemerick.url :refer [url]]
            [goog.object :as gobj]
            [re-frame.core :as re-frame]
            [re-frame-request.events :as rfr-events]

            [kyber.events.utils :refer [base-uri add-auth kyber-event-fx]]
            [kyber.i18n.core :as i18n]
            [kyber.interceptors.analytics :refer [update-application-context]]
            [kyber.lib.error-reporting :as ke]
            [kyber.constants :refer [cognito-user-pool-id
                                     cognito-client-id
                                     aws-region
                                     user-pool-domain
                                     refresh-token-polling-interval
                                     token-refresh-interval
                                     initial-location
                                     base-api-url]]
            [kyber.utils :as ku]
            [kyber.feature-flags :refer [feature-enabled?]]))

(def AWS
  (.-AWS js/window))
(def AmazonCognitoIdentity
  (.-AmazonCognitoIdentity js/window))
(def AttributeConstructor
  (.-CognitoUserAttribute AmazonCognitoIdentity))
(def AuthenticationConstructor
  (.-AuthenticationDetails AmazonCognitoIdentity))
(def UserConstructor
  (.-CognitoUser AmazonCognitoIdentity))
(def CognitoIdentityCredentials
  (.-CognitoIdentityCredentials AWS))

(def ->cognito-attr
  #(AttributeConstructor. (clj->js %)))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Passwordless SignIn
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-passwordless-sign-in-success
    {:description
     "Requests passwordless email (Success)"
     :changelog
     [{:date "08APR20"
       :event-version 0
       :data {}}]}))

(defn passwordless-sign-in-success
  [{:keys [db]} [event-name {:keys [email]}]]
  {:db (assoc db :passwordless-email-sent-to email)
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                (when (some? (:passwordless-email-sent-to db))
                  [:notifier/create-toast
                   {:message (i18n/t* ::resent-message)}])]})

(when ^boolean js/goog.DEBUG
  (def event-doc-passwordless-sign-in-failure
    {:description
     "Requests passwordless email (Failure)"
     :changelog
     [{:date "08APR20"
       :event-version 0
       :data {}}]}))

(defn passwordless-sign-in-failure
  [_ [event-name]]
  {:dispatch [:analytics/save-datapoint
              {:event event-name
               :event-version 0}]})

(when ^boolean js/goog.DEBUG
  (def event-doc-passwordless-sign-in
    {:description
     "Requests passwordless email"
     :changelog
     [{:date "08APR20"
       :event-version 0
       :data {}}]}))

(defn passwordless-sign-in
  [{:keys [db]} [event-name payload]]
  (let [redirect-path (get-in db [:router :route-query "redirect"])
        is-root?      (= "/" redirect-path)
        params
        (-> payload
            (assoc :redirect
                   ;; Part of non-optimal workaround for redirect bug
                   ;; https://github.com/Archemedx/kyber/issues/7156
                   (if is-root?
                     (:pathname @initial-location)
                     redirect-path)))]
    (cond-> {:request {:name event-name
                       :method :post
                       :uri (base-uri db (str "/v1/send-login-email"))
                       :format :json
                       :response-format :json
                       :params (select-keys params [:email :redirect :locale-key])
                       :on-success [:authentication/passwordless-sign-in-success
                                    (select-keys params [:email])]
                       :on-failure [:authentication/passwordless-sign-in-failure]}
             :dispatch-n [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]]}
      is-root?
      (update :dispatch-n conj [:report-error
                                {:name     "Redirect Error: redirect path is badly formed '/'"
                                 :tab-name "Initial Location"
                                 :payload  {:initial-location @initial-location}}]))))

(when ^boolean js/goog.DEBUG
  (def event-doc-passwordless-sign-in-reset
    {:description
     "Clears the password the email was sent to so a new one can be sent"
     :changelog
     [{:date "08APR20"
       :event-version 0
       :data {}}]}))

(defn passwordless-sign-in-reset
  [{:keys [db]} [event-name]]
  {:db (assoc db :passwordless-email-sent-to nil)
   :dispatch [:analytics/save-datapoint
              {:event event-name
               :event-version 0}]})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; SIGN-UP
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-up-success
    {:description
     "User received a successful response from the server when signing up"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-up-success
  [{:keys [db]} [event-name _response redirect request-name request-time]]
  {:db db
   :dispatch-n [[:notifier/create-alert
                 ;; TODO what happens if there is a subscribe in an event?
                 {:title (i18n/t* ::thanks-for-signing-up)
                  :message (i18n/t* ::check-inbox)
                  :confirm-only true}]
                [:router/nav-to (cond-> "/sign-in"
                                  (some? redirect)
                                  (str "?redirect=" redirect))]

                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:request/done
                 {:status :success
                  :name request-name
                  :error nil
                  :request-time request-time}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-up-failure
    {:description
     "User received a failed response from the server when signing up"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-up-failure
  [{:keys [db]} [event-name err request-name request-time]]
  {:db db
   :dispatch-n [;; These are known errors like (UsernameExists)
                #_[:report-error (ke/->failed-request-error event-name)]
                [:notifier/create-toast
                 {:status :error
                  :message (.-message err)}]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:request/done
                 {:status :failure
                  :name request-name
                  :error (js->clj err :keywordize-keys true)
                  :request-time request-time}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-up
    {:description
     "User is requesting to sign up"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-up
  [{:keys [db]} [event-name {:keys [email
                                    password
                                    redirect
                                    first-name
                                    last-name]}]]
  {:db db
   :dispatch [:analytics/save-datapoint
              {:event event-name
               :event-version 0}]
   :cognito-request
   {:request-name event-name
    :action :sign-up
    :user-pool (get-in db [:configuration :cognito-user-pool])
    :payload {:password password
              :email (str/lower-case email)
              :first-name first-name
              :last-name last-name}
    :redirect redirect}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; SIGN-IN
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-in-success
    {:description
     "User received a successful response from the server when signing in"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-in-success
  [{:keys [db]} [event-name _ request-name request-time]]
  {:db db
   :authentication/set-has-signed-in-flag nil
   ;; Add timeout to :authentication/get-session because
   ;; cognito may not have had time to save the session to
   ;; localStorage yet
   :dispatch-n [[:dispatch-in 100 :authentication/get-session]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:request/done
                 {:status :success
                  :name request-name
                  :error nil
                  :request-time request-time}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-in-failure
    {:description
     "User received a failed response from the server when signing in"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-in-failure
  [{:keys [resend-verification-code]}]
  (fn [{:keys [db]} [event-name err email request-name request-time]]
    (cond-> {:db db
             :dispatch-n [;; These are known errors like "IncorrectPassword"
                          #_[:report-error
                             (ke/->failed-request-error event-name)]
                          [:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]
                          [:request/done
                           {:status :failure
                            :name request-name
                            :error (js->clj err :keywordize-keys true)
                            :request-time request-time}]]}
      (errors/user-not-confirmed-exception? err)
      (update :dispatch-n
              conj
              [:notifier/create-alert
               {:title (i18n/t* ::email-not-verified)

                :message (i18n/t* ::send-another)

                :confirm-action (resend-verification-code email)}])

      (not (errors/user-not-confirmed-exception? err))
      (update :dispatch-n
              conj
              [:notifier/create-toast
               {:status :error
                :message (.-message err)}]))))

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-in
    {:description
     "User requested to sign in"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-in
  [{:keys [db]} [event-name {:keys [email password]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:request-name event-name
    :action :sign-in
    :user-pool (get-in db [:configuration :cognito-user-pool])
    :payload {:password password
              :username (str/lower-case email)}}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; RESEND VERIFICATION CODE
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-resend-verification-code-success
    {:description
     "User received a successful response for requesting verification code"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn resend-verification-code-success
  [{:keys [db]} [event-name _response]]
  {:db db
   :dispatch-n [[:notifier/create-toast
                 {:message (i18n/t* ::verification-sent)}]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-resend-verification-code-success
    {:description
     "User received a failed response for requesting verification code"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn resend-verification-code-failure
  [{:keys [db]} [event-name err]]
  {:db db
   :dispatch-n [[:notifier/create-toast
                 {:status :error
                  :message (.-message err)}]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-resend-verification-code
    {:description
     "User requested to resend verification code"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn resend-verification-code
  [{:keys [db]} [event-name {:keys [email]}]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:action :resend-verification-code
    :user-pool (get-in db [:configuration :cognito-user-pool])
    :payload {:username email}}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Redirect To Identity Provider
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-redirect-to-idp
    {:description
     "Returns a user to an idp"
     :changelog
     [{:date "14NOV18"
       :event-version 0
       :data {}}]}))

(defn- redirect-to-idp
  [{:keys [db]} [event-name {:keys [idp-uuid activity-uuid]}]]
  ;; Part of non-optimal workaround for redirect bug
  ;; https://github.com/Archemedx/kyber/issues/7156
  (let [current-pathname (gobj/getValueByKeys js/window "location" "pathname")
        is-root? (= "/" current-pathname)
        actual-pathname (js/encodeURIComponent
                         (if is-root?
                           (:pathname @initial-location)
                           current-pathname))]
    (cond-> {:db db
             :dispatch-n [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]]
             :change-window-location (cond-> (str base-api-url
                                                  "/v1/sso-redirect?identity-provider=" idp-uuid
                                                  "&redirect-uri=" actual-pathname)
                                       (some? activity-uuid)
                                       (str "&activity-uuid=" activity-uuid))}
      is-root?
      (update :dispatch-n conj [:report-error
                                {:name "Redirect Error: redirect path is badly formed '/'"
                                 :tab-name "Initial Location"
                                 :payload {:initial-location @initial-location}}]))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; FOLLOW INTERNAL REDIRECT
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-follow-internal-redirect
    {:description
     "Follows a redirect managed in state"
     :changelog
     [{:date "19JUL19"
       :event-version 0
       :data {}}]}))

(defn follow-internal-redirect
  [{:keys [db]} [event-name _]]
  (let [auth-redirect (-> "auth-redirect"
                          ku/get-item
                          ku/clean-url)]
    {:db db
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]
                  [:dispatch-in 300 :router/nav-to auth-redirect]]
     :authentication/remove-auth-redirect nil
     :invoke #(when (feature-enabled? :console-log)
                (js/console.log "auth-redirect: " auth-redirect))}))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; COMPLETE SESSION INITIALIZATION
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-complete-session-initialization
    {:description
     "Session initialization has completed (user is synced with cognito)"
     :changelog
     [{:date "08NOV18"
       :event-version 0
       :data {}}]}))

(defn- complete-session-initialization
  [{:keys [db]} [event-name _]]
  (let [on-session-success
        (get-in db [:custom-session-hooks :on-session-success])

        auth-redirect
        (ku/get-item "auth-redirect")

        has-initialized (get-in db [:session :has-initialized] false)

        ;; TODO This should not be in the shared authentication module
        ;;      when we have time move into on-session-success or better
        ;;      location.
        is-authenticated
        (re-frame/subscribe [:authentication/is-authenticated?])

        {:keys [activity-uuid]}
        (get-in db [:router :route-params])

        identity-provider
        (get-in db [:router :route-query "identity-provider"])

        reset-code
        (get-in db [:router :route-query "reset-code"])

        should-redirect-to-idp
        (and (false? @is-authenticated)
             (some? activity-uuid)
             (some? identity-provider))

        should-redirect-internal
        (and (some? auth-redirect)
             (true? @is-authenticated)
             (nil? reset-code))]

    (cond-> {:db (cond-> db
                   (and (false? has-initialized)
                        (false? should-redirect-to-idp))
                   (assoc-in [:session :has-initialized] true))
             :dispatch-n []
             :invoke #(when (feature-enabled? :console-log)
                        (js/console.log "auth-redirect" auth-redirect)
                        (js/console.log "has-initialized" has-initialized)
                        (js/console.log "@is-authenticated" @is-authenticated)
                        (js/console.log "activity-uuid" activity-uuid)
                        (js/console.log "identity-provider" identity-provider)
                        (js/console.log "reset-code" reset-code)
                        (js/console.log "should-redirect-to-idp" should-redirect-to-idp)
                        (js/console.log "should-redirect-internal" should-redirect-internal))}
      ;; Only dispatch on first call to `complete-session-initialization`
      (false? has-initialized)
      (update :dispatch-n conj [:analytics/save-datapoint
                                {:event event-name
                                 :event-version 0}])

      should-redirect-to-idp
      (update :dispatch-n conj
              [:authentication/redirect-to-idp
               {:idp-uuid identity-provider
                :activity-uuid activity-uuid}])

      ;; If reset code is present than the user came from an email and we
      ;; should allow default routing to take precedence over custom logic
      ;; and re route logic
      (and (some? on-session-success)
           (nil? reset-code))
      (update :dispatch-n conj [:authentication/on-session-success-hook
                                on-session-success])

      (true? should-redirect-internal)
      (update :dispatch-n conj [:authentication/follow-internal-redirect]))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; GET SESSION
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-on-session-success-hook
    {:description
     "Custom hook for on session success"
     :changelog
     [{:date "13SEP18"
       :event-version 0
       :data {}}]}))

(defn- on-session-success-hook
  [{:keys [db]} [event-name on-session-success]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :authentication/on-session-success {:on-session-success on-session-success
                                       :db db}})

(when ^boolean js/goog.DEBUG
  (def event-doc-get-session-success
    {:description
     "User session is valid and returned"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn- cognito-groups [user]
  (-> user
      (js-invoke "getAccessToken")
      (goog.object/getValueByKeys "payload" "cognito:groups")
      (js->clj)
      (set)))

(defn get-session-success
  [{:keys [db]} [event-name session current-user]]
  {:db (-> db
           (assoc-in [:session :cognito-session] session)
           (assoc-in [:session :refresh-time] (+ (.getTime (js/Date.))
                                                 token-refresh-interval))
           (assoc-in [:session :user :groups] (cognito-groups session)))

   :authentication/start-refresh-poll
   {:interval refresh-token-polling-interval}

   :cognito-request {:action :get-user-attributes
                     :current-user current-user}

   :dispatch-n
   [[:analytics/save-datapoint
     {:event event-name
      :event-version 0}]
    [:authentication/get-self]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-get-session-failure
    {:description
     "User session is invalid"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn get-session-failure
  [{:keys [db]} [event-name err]]
  (let [pathname (-> js/window .-location .-pathname)
        {:keys [curriculum-uuid activity-uuid]} (get-in db [:router :route-params])
        sign-in-url (cond
                      (str/starts-with? pathname "/admin")
                      "/admin"
                      
                      (and (some? curriculum-uuid) (nil? activity-uuid))
                      (str "/curriculum/" curriculum-uuid)
                      
                      (and (some? curriculum-uuid) (some? activity-uuid))
                      (str "/curriculum/" curriculum-uuid "/activity/" activity-uuid))]
    {:db db
     :dispatch-n (cond-> [[:report-error (ke/->failed-request-error event-name)]
                          [:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]]

                   (errors/not-authorized-exception? err)
                   (conj [:authentication/sign-out {:sign-in-url sign-in-url}]))}))

(when ^boolean js/goog.DEBUG
  (def event-doc-get-session
    {:description
     "User session is requested"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn get-session
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:action :get-session
    :user-pool (get-in db [:configuration :cognito-user-pool])}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; GET SELF
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-self-success
    {:description "User permissions successfully returned"
     :changelog [{:date "17APR19"
                  :event-version 0
                  :data {}}]}))

(defn get-self-success
  [{:keys [db]} [event-name {:keys [permissions email-unsubscribe]}]]
  {:db (-> db (update-in [:session :user]
                         merge
                         {:permissions permissions
                          :email-unsubscribe email-unsubscribe}))
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:authentication/complete-session-initialization]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-get-self-failure
    {:description "User permissions failed to return"
     :changelog [{:date "17APR19"
                  :event-version 0
                  :data {}}]}))

(defn get-self-failure
  [{:keys [db]} [event-name _error]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-get-self
    {:description "Request to fetch user permissions"
     :changelog [{:date "17APR19"
                  :event-version 0
                  :data {}}]}))

(defn get-self
  [{:keys [db]} [event-name _]]
  {:db db
   :request {:name event-name
             :method :get
             :uri (base-uri db (str "/v2/admin/users/self"))
             :format :url
             :response-format :json
             :headers (add-auth db)
             :on-success [:authentication/get-self-success]
             :on-failure [:authentication/get-self-failure]}
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; REFRESH CREDENTIALS
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-refresh-credentials-success
    {:description
     "User credential refresh was successful"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn refresh-credentials-success
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-refresh-credentials-failure
    {:description
     "User credential refresh failed"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn refresh-credentials-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-refresh-credentials
    {:description
     "Credential refresh requested"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn refresh-credentials
  [{:keys [db]} [event-name credentials]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request {:action :refresh-credentials
                     :credentials credentials}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Authenticate Cognito Token
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-authenticate-cognito-token-success
    {:description
     "Request token authentication successful"
     :changelog
     [{:date "04SEP18"
       :event-version 0
       :data {}}]}))

(defn authenticate-cognito-token-success
  [{:keys [db]} [event-name {:keys [id_token access_token refresh_token]}]]
  {:db db
   :authentication/set-tokens {:id-token id_token
                               :access-token access_token
                               :refresh-token refresh_token}
   :authentication/strip-tokens nil
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-authenticate-cognito-token-error
    {:description
     "Request token authentication error"
     :changelog
     [{:date "04SEP18"
       :event-version 0
       :data {}}]}))

(defn authenticate-cognito-token-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-authenticate-cognito-token
    {:description
     "Request token authentication"
     :changelog
     [{:date "04SEP18"
       :event-version 0
       :data {}}]}))

(defn authenticate-cognito-token
  [{:keys [db]} [event-name code]]
  (let [redirect-uri ;; TODO: #6040 Short-term fix until we can unify client-side routing
        (let [pathname (-> js/window .-location .-pathname)
              origin (-> js/window .-location .-origin)]
          (cond-> origin
            (str/starts-with? pathname "/admin")
            (str "/admin")))]
    {:db db
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]]
     :request {:name :authentication/authenticate-cognito-token
               :method :post
               :uri (str user-pool-domain "/oauth2/token")
               :format :url
               :response-format :json
               :params {:grant_type "authorization_code"
                        :code code
                        :client_id cognito-client-id
                        :redirect_uri redirect-uri}
               :headers {}
               :on-success [:authentication/authenticate-cognito-token-success]
               :on-failure [:authentication/authenticate-cognito-token-failure]}
     :invoke #(when (feature-enabled? :console-log)
                (js/console.log "redirect-uri: " redirect-uri))}))


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; FORGOT PASSWORD
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-forgot-password-success
    {:description
     "Request for password reset email succeeded"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn forgot-password-success
  [{:keys [db]} [event-name _data redirect]]
  {:db db
   :dispatch-n [[:notifier/create-toast
                 {:message (i18n/t* ::reset-sent)}]
                [:router/nav-to (cond-> "/sign-in"
                                  (some? redirect)
                                  (str "?redirect=" redirect))]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-forgot-password-failure
    {:description
     "Request for password reset email failed"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn forgot-password-failure
  [{:keys [resend-verification-code]}]
  (fn [{:keys [db]} [event-name err email]]
    (cond-> {:db db
             :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                          [:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]]}

      (errors/invalid-parameter-exception? err)
      (update :dispatch-n
              conj
              [:notifier/create-alert
               {:title (i18n/t* ::email-not-verified)

                :message (i18n/t* ::send-another-verification)
                :confirm-action (resend-verification-code email)}])

      (not (errors/invalid-parameter-exception? err))
      (update :dispatch-n
              conj
              [:notifier/create-toast
               {:status :error
                :message (.-message err)}]))))

(when ^boolean js/goog.DEBUG
  (def event-doc-forgot-password
    {:description
     "User requested a password reset"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn forgot-password
  [{:keys [db]} [event-name {:keys [email redirect]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:action :forgot-password
    :username email
    :user-pool (get-in db [:configuration :cognito-user-pool])
    :redirect redirect}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; RESET PASSWORD
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-reset-password-success
    {:description
     "User password has been reset"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn reset-password-success
  [{:keys [db]} [event-name credentials]]
  (let [auth-redirect (ku/get-item "auth-redirect")]
    {:db db
     :dispatch-n (cond-> [[:authentication/sign-in credentials]
                          [:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]]
                   (some? auth-redirect)
                   (conj [:authentication/follow-internal-redirect]))}))

(when ^boolean js/goog.DEBUG
  (def event-doc-reset-password-failure
    {:description
     "User password failed to reset"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn reset-password-failure
  [{:keys [db]} [event-name err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:notifier/create-toast
                 {:status :error
                  :message (.-message err)}]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-reset-password
    {:description
     "User submitted a request to update their password"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn reset-password
  [{:keys [db]} [event-name {:keys [email reset-code password]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:action :reset-password
    :reset-code reset-code
    :password password
    :username (str/lower-case email)
    :user-pool (get-in db [:configuration :cognito-user-pool])}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; CLEAR COOKIES
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-clear-cookies-success
    {:description
     "Makes request to clear secure cookies (success)"
     :changelog
     [{:date "25SEP19"
       :event-version 0
       :data {}}]}))

(defn clear-cookies-success
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-clear-cookies-failure
    {:description
     "Makes request to clear secure cookies (failure)"
     :changelog
     [{:date "25SEP19"
       :event-version 0
       :data {}}]}))

(defn clear-cookies-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-clear-cookies
    {:description
     "Makes request to clear secure cookies"
     :changelog
     [{:date "25SEP19"
       :event-version 0
       :data {}}]}))

(defn clear-cookies
  [{:keys [db]} [event-name _]]
  {:db db
   :request {:name event-name
             :method :get
             :uri (base-uri db "/v1/logout")
             :format :url
             :response-format :json
             :headers (add-auth db)
             :on-success [:authentication/clear-cookies-success]
             :on-failure [:authentication/clear-cookies-failure]}
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; SIGN-OUT
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
(defn sign-out-fx
  [reset-db]
  (fn [{:keys [username user-pool]}]
    (re-frame/dispatch [:authentication/clear-cookies])

    (if (or (nil? username)
            (nil? user-pool))
      (reset-db)
      (let [cognito-user (UserConstructor. (clj->js {:Username username
                                                     :Pool user-pool}))]
        (.signOut ^js cognito-user)
        (reset-db)))))

(when ^boolean js/goog.DEBUG
  (def event-doc-sign-out
    {:description
     "User signed out"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn sign-out
  [{:keys [db]} [event-name {:keys [sign-in-url]}]]
  (let [homepage-url (get-in db [:activity :provider :homepage-url])]
    (cond->
     {:db db
      :authentication/sign-out
      {:username (get-in db [:session :user :email])
       :user-pool (get-in db [:configuration :cognito-user-pool])}
      :dispatch-n [(when-not homepage-url
                     [:router/nav-to (or sign-in-url "/")])

                   (when-not homepage-url
                     [:authentication/complete-session-initialization])

                   [:analytics/save-datapoint
                    {:event event-name
                     :event-version 0}]]}
      (some? homepage-url)
      (assoc :location/set! homepage-url))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; GET USER ATTRIBUTES
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-user-attributes-success
    {:description
     "User attributes were successfully retrieved"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn get-user-attributes-success
  [{:keys [db]} [event-name attributes]]
  (let [attributes (reduce (fn [acc ^js attr]
                             (let [name (-> attr
                                            .-Name
                                            (clojure.string/replace #"_" "-")
                                            (keyword))]
                               (assoc acc name (.-Value attr))))
                           {}
                           attributes)]

    {:db (update-in db
                    [:session :user]
                    merge
                    (cond-> attributes
                      (some? (:identities attributes))
                      (update :identities #(js->clj (.parse js/JSON %)
                                                    :keywordize-keys true))))
     :bootstrap/set-logrocket-user attributes
     :dispatch-n [[:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]]}))

(when ^boolean js/goog.DEBUG
  (def event-doc-get-user-attributes-failure
    {:description
     "User attributes retrieval failed"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn get-user-attributes-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; UPDATE USER ATTRIBUTES
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-user-preferences-success
    {:description
     "User preferences were successfully updated"
     :changelog
     [{:date "05AUG19"
       :event-version 0
       :data {}}]}))

(defn update-user-preferences-success
  [{:keys [db]} [event-name preferences]]
  {:db (update-in db [:session :user] merge preferences)
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast {:message "Preferences Updated"}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-update-user-preferences-failure
    {:description
     "User preferences failed to update"
     :changelog
     [{:date "05AUG19"
       :event-version 0
       :data {}}]}))

(defn update-user-preferences-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast
                 {:message (i18n/t* ::could-not-save-changes)
                  :status :error}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-update-user-preferences
    {:description
     "User preferences update request"
     :changelog
     [{:date "05AUG19"
       :event-version 0
       :data {}}]}))

(defn update-user-preferences
  [{:keys [db]} [event-name preferences]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :request {:name event-name
             :method :post
             :uri (base-uri db (str "/v1/self/preferences"))
             :format :json
             :response-format :json
             :params preferences
             :headers (add-auth db)
             :on-success [:authentication/update-user-preferences-success]
             :on-failure [:authentication/update-user-preferences-failure]}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; UPDATE USER PASSWORD
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-user-password-success
    {:description
     "User password successfully updated"
     :changelog
     [{:date "24AUG18"
       :event-version 0
       :data {}}]}))

(defn update-user-password-success
  [{:keys [db]} [event-name _attributes]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast {:message (i18n/t* ::password-updated)}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-update-user-password-failure
    {:description
     "User password failed to update"
     :changelog
     [{:date "24AUG18"
       :event-version 0
       :data {}}]}))

(defn update-user-password-failure
  [{:keys [db]} [event-name err]]
  (let [error-message
        (condp = (-> err
                     (js->clj :keywordize-keys true)
                     :code
                     keyword)
          :NotAuthorizedException
          (str (i18n/t* ::current-password-incorrect))

          :LimitExceededException
          (str (i18n/t* ::too-many-change-attempts))

          (js/console.log err))]
    {:db db
     :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                  [:analytics/save-datapoint
                   {:event event-name
                    :event-version 0}]
                  [:notifier/create-toast
                   {:message (or error-message
                                 (i18n/t* ::password-not-saved))
                    :status :error}]]}))

(defn update-user-password
  [{:keys [db]} [event-name {:keys [current-password new-password]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]
   :cognito-request
   {:action :update-user-password
    :current-password current-password
    :new-password new-password
    :user-pool (get-in db [:configuration :cognito-user-pool])}})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Refresh Tokens
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-refresh-tokens-success
    {:description
     "Cognito successfully updated the id-token & access-token"
     :changelog
     [{:date "02JUL18"
       :event-version 0
       :data {}}]}))

(defn refresh-tokens-success
  [{:keys [db]} [event-name session]]
  {:db (assoc-in db [:session :cognito-session] session)
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(when ^boolean js/goog.DEBUG
  (def event-doc-refresh-tokens-failure
    {:description
     "Cognito failed to update the id-token & access-token"
     :changelog
     [{:date "02JUL18"
       :event-version 0
       :data {}}]}))

(defn refresh-tokens-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:report-error (ke/->failed-request-error event-name)]
                [:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]]})

(defn poll-for-session-refresh
  [{:keys [db]} _]
  (let [session ^js (get-in db [:session :cognito-session])
        user ^js (.getCurrentUser
                  (get-in db [:configuration :cognito-user-pool]))
        current-time (.getTime (js/Date.))
        refresh-time (get-in db [:session :refresh-time])
        should-refresh (and (> current-time refresh-time)
                            (some? user))]
    (if (true? should-refresh)
      {:db (assoc-in db [:session :refresh-time]
                     (+ current-time
                        token-refresh-interval))
       :invoke (fn []
                 (.refreshSession
                  user
                  (.getRefreshToken session)
                  (fn [error session]
                    (if error
                      (re-frame/dispatch
                       [:authentication/refresh-tokens-failure error])
                      (re-frame/dispatch
                       [:authentication/refresh-tokens-success session])))))}
      {:db db})))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Cognito
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-configure-cognito
    {:description
     "Congnito configuration setup"
     :changelog
     [{:date "20JUN18"
       :event-version 0
       :data {}}]}))

(defn parse-saml-state
  [state-string]
  (->> (clojure.string/split state-string #"\+")
       (map #(clojure.string/split % #"="))
       (reduce (fn [acc [key val]]
                 (assoc acc (keyword key) val))
               {})))

(def ^{:private true} linking-error-message
  "Already+found+an+entry+for+username+")

(defn configure-cognito
  [{:keys [db]} [event-name {:keys [session-options]
                             :or {session-options {}}}]]
  (let [{;; Token Authentication
         access-token "access-token"
         refresh-token "refresh-token"
         id-token "id-token"

         ;; SAML Authentication
         saml-state "state"

         ;; Oauth2 Authentication
         code "code"

         error-description "error_description"}
        (get-in db [:router :route-query])

        {:keys [redirect]}
        (when (some? saml-state)
          (parse-saml-state saml-state))

        user-pool-constructor
        (-> js/window
            .-AmazonCognitoIdentity
            .-CognitoUserPool)]

    (cond-> {:db (cond-> (update db :configuration merge
                                 {:cognito-client-id
                                  cognito-client-id

                                  :cognito-user-pool
                                  (user-pool-constructor.
                                   (clj->js {:UserPoolId cognito-user-pool-id
                                             :ClientId cognito-client-id}))

                                  :cognito-user-pool-id
                                  cognito-user-pool-id})
                   (seq session-options)
                   (assoc :custom-session-hooks session-options))
             :dispatch-n [[:analytics/save-datapoint
                           {:event event-name
                            :event-version 0}]

                          (cond
                            ;; Token Authentication
                            (and (some? access-token)
                                 (some? refresh-token)
                                 (some? id-token))
                            [:authentication/authenticate-cognito-token-success
                             {:id_token id-token
                              :access_token access-token
                              :refresh_token refresh-token}]

                            ;; OAuth2 Authentication
                            (some? code)
                            [:authentication/authenticate-cognito-token code]

                            :else
                            [:authentication/get-session])]
             :invoke #(when (feature-enabled? :console-log)
                        (js/console.log "redirect: " redirect))}
      (some? redirect)
      (assoc :local-storage-set-item {:key "auth-redirect"
                                      :value redirect})

      (and (string? error-description)
           (str/includes? error-description linking-error-message))
      (assoc :location/set!
             (base-uri db (str "/v2/relogin"
                               (when (some? redirect)
                                 (str "?redirect="
                                      (js/encodeURIComponent redirect)))))))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Cognito Request Dispatcher
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
(defmulti cognito-request
  #(do
     (when (feature-enabled? :trace-events)
       (js/console.log "[cognito event]" (:action (first %))))
     (:action (first %))))

(defmethod cognito-request :sign-up
  [[{:keys [payload user-pool redirect request-name]} dispatch]]
  (let [{:keys [password email first-name last-name]}
        payload

        attribute-list
        (clj->js (map ->cognito-attr (cond-> [{:Name "email"
                                               :Value email}]
                                       (some? last-name)
                                       (conj {:Name "family_name"
                                              :Value last-name})

                                       (some? first-name)
                                       (conj {:Name "given_name"
                                              :Value first-name}))))

        request-time
        (.getTime (js/Date.))]

    (rfr-events/track-request! request-name request-time)

    (.signUp
     user-pool
     email
     password
     attribute-list
     nil
     (fn [err result]
       (if-not (nil? err)
         (dispatch [:authentication/sign-up-failure err
                    request-name
                    request-time])
         (dispatch [:authentication/sign-up-success result
                    redirect
                    request-name
                    request-time]))))))

(defmethod cognito-request :sign-in
  [[{:keys [payload user-pool request-name]} dispatch]]
  (let [{:keys [username password]}
        payload

        authentication-details
        (AuthenticationConstructor.
         (clj->js {:Username username
                   :Password password}))

        cognito-user
        (UserConstructor. (clj->js {:Username username
                                    :Pool user-pool}))

        request-time
        (.getTime (js/Date.))]

    (rfr-events/track-request! request-name request-time)

    (.authenticateUser
     cognito-user
     authentication-details
     (clj->js
      {:onSuccess
       #(dispatch [:authentication/sign-in-success %
                   request-name
                   request-time])
       :onFailure
       #(dispatch [:authentication/sign-in-failure %
                   username
                   request-name
                   request-time])}))))

(defmethod cognito-request :resend-verification-code
  [[{:keys [payload user-pool]} dispatch]]
  (let [{:keys [username]} payload
        cognito-user (UserConstructor. (clj->js {:Username username
                                                 :Pool user-pool}))]
    (.resendConfirmationCode
     cognito-user
     (fn [err result]
       (if-not (nil? err)
         (dispatch
          [:authentication/resend-verification-code-failure err])
         (dispatch
          [:authentication/resend-verification-code-success result]))))))

(defmethod cognito-request :get-session
  [[{:keys [user-pool]} dispatch]]
  (let [current-user ^js (.getCurrentUser user-pool)]
    (if current-user
      (.getSession
       current-user
       (fn [err ^js result]
         (if-not (nil? err)
           (dispatch [:authentication/get-session-failure err])
           (.refreshSession
            current-user
            (.-refreshToken result)
            (fn [err session]
              (if err
                (dispatch [:authentication/get-session-failure err])
                (dispatch [:authentication/get-session-success
                           session
                           current-user])))))))
      (dispatch [:authentication/complete-session-initialization]))))

(defmethod cognito-request :get-user-attributes
  [[{:keys [^js current-user]} dispatch]]
  (.getSession
   current-user
   (fn [err _]
     (if-not (nil? err)
       (dispatch
        [:authentication/get-user-attributes-failure err])

       (.getUserAttributes
        current-user
        (fn [err attributes]
          (if-not (nil? err)
            (dispatch
             [:authentication/get-user-attributes-failure err])

            (dispatch
             [:authentication/get-user-attributes-success attributes]))))))))

(defmethod cognito-request :update-user-password
  [[{:keys [user-pool new-password current-password]} dispatch]]
  (let [^js current-user (.getCurrentUser user-pool)]
    (.getSession
     current-user
     (fn [err _]
       (if-not (nil? err)
         (dispatch
          [:authentication/update-user-password-failure err])

         (.changePassword
          current-user
          current-password
          new-password
          (fn [err _]
            (if-not (nil? err)
              (dispatch
               [:authentication/update-user-password-failure err])
              (dispatch
               [:authentication/update-user-password-success])))))))))


(defmethod cognito-request :refresh-credentials
  [[{:keys [credentials]} dispatch]]
  (.refresh
   credentials
   (fn [err]
     (if-not (nil? err)
       (dispatch
        [:authentication/refresh-credentials-failure err])
       (dispatch
        [:authentication/refresh-credentials-success])))))

(defmethod cognito-request :forgot-password
  [[{:keys [username user-pool redirect]} dispatch]]
  (let [cognito-user (UserConstructor. (clj->js {:Username username
                                                 :Pool user-pool}))]
    (.forgotPassword
     cognito-user
     (clj->js
      {:onSuccess
       #(dispatch [:authentication/forgot-password-success % redirect])
       :onFailure
       #(dispatch [:authentication/forgot-password-failure % username])}))))

(defmethod cognito-request :reset-password
  [[{:keys [username user-pool password reset-code]} dispatch]]
  (let [cognito-user (UserConstructor. (clj->js {:Username username
                                                 :Pool user-pool}))]
    (.confirmPassword
     cognito-user
     reset-code
     password
     (clj->js
      {:onSuccess
       #(dispatch [:authentication/reset-password-success {:email username
                                                           :password password}])
       :onFailure
       #(dispatch [:authentication/reset-password-failure %])}))))

(defn initialize-aws-services
  "Registers AWS service events"
  []
  (re-frame/reg-fx
   :aws-config
   #(goog.object/set (.-config AWS) "region" aws-region)))

(defn set-tokens
  [{:keys [access-token refresh-token id-token]}]
  (let [idToken ^js (js/AmazonCognitoIdentity.CognitoIdToken.
                     (clj->js {:IdToken id-token}))
        accessToken ^js (js/AmazonCognitoIdentity.CognitoAccessToken.
                         (clj->js {:AccessToken access-token}))
        _refreshToken ^js (js/AmazonCognitoIdentity.CognitoRefreshToken.
                           (clj->js {:RefreshToken refresh-token}))

        sub (gobj/getValueByKeys idToken "payload" "sub")

        ;; Keys
        key-prefix (str "CognitoIdentityServiceProvider." cognito-client-id)
        id-token-key (str key-prefix "." sub ".idToken")
        access-token-key (str key-prefix "." sub ".accessToken")
        refresh-token-key (str key-prefix "." sub ".refreshToken")
        clock-drift-key (str key-prefix "." sub ".clockDrift")
        last-user-key (str key-prefix ".LastAuthUser")

        ;; Clock drift
        now (.floor js/Math (/ (js/Date.) 1000))
        iat (.min js/Math
                  (.getIssuedAt accessToken)
                  (.getIssuedAt idToken))
        clock-drift (- now iat)]

    (ku/set-item id-token-key id-token {:json false})
    (ku/set-item access-token-key access-token {:json false})
    (ku/set-item refresh-token-key refresh-token {:json false})
    (ku/set-item clock-drift-key clock-drift {:json false})
    (ku/set-item last-user-key sub {:json false})

    (re-frame/dispatch [:authentication/get-session])))

(defn strip-tokens
  "If authentication tokens are present in the query parameter they
  should be removed"
  [_]
  (let [current-url
        (-> js/window .-location .-href url)

        {access-token "access-token"
         refresh-token "refresh-token"
         id-token "id-token"}
        (:query current-url)]

    (when (or (some? access-token)
              (some? refresh-token)
              (some? id-token))
      (set! (.-location js/window)
            (str (update current-url
                         :query #(dissoc %
                                         "access-token"
                                         "refresh-token"
                                         "id-token")))))))

(defn- set-auth-redirect
  [should-set-auth-redirect]
  (when (true? should-set-auth-redirect)
    (let [auth-redirect
          (ku/get-item "auth-redirect")

          {:keys [query] :as current-url}
          ;; Consider changing to (.. js/window -location -href)
          (url (gobj/getValueByKeys js/window "location" "href"))

          should-update-url
          (and (some? auth-redirect)
               (nil? (get query "redirect")))]

      (when (true? should-update-url)
        (set! (.-location js/window)
              (str (assoc-in current-url
                             [:query "redirect"]
                             auth-redirect)))))))

(defn initialize-authentication
  "Registers Kyber authentication events"
  []

  (re-frame/reg-fx :authentication/set-tokens set-tokens)

  (re-frame/reg-fx :authentication/strip-tokens strip-tokens)

  (re-frame/reg-fx
   :authentication/sign-out
   (sign-out-fx #(re-frame/dispatch [:db/reset])))

  (re-frame/reg-fx
   :authentication/set-has-signed-in-flag
   (fn [_]
     (ku/set-item "hasSignedIn" true)))

  (re-frame/reg-event-fx
   :authentication/poll-for-session-refresh
   poll-for-session-refresh)

  (re-frame/reg-fx
   :authentication/start-refresh-poll
   (fn [{:keys [interval]}]
     (.setInterval js/window #(re-frame/dispatch
                               [:authentication/poll-for-session-refresh])
                   interval)))

  (re-frame/reg-fx
   :cognito-request
   #(cognito-request [% re-frame/dispatch]))

  (re-frame/reg-fx
   :authentication/remove-auth-redirect
   #(ku/set-item "auth-redirect" nil))

  (re-frame/reg-fx
   :authentication/on-session-success
   (fn [{:keys [on-session-success db]}]
     (on-session-success db)))

  (re-frame/reg-fx
   :authentication/set-auth-redirect
   set-auth-redirect)

  (kyber-event-fx
   :authentication/configure-cognito
   configure-cognito)

  (kyber-event-fx
   :authentication/sign-up-success
   sign-up-success)

  (kyber-event-fx
   :authentication/sign-up-failure
   sign-up-failure)

  (kyber-event-fx
   :authentication/sign-up
   sign-up)

  (kyber-event-fx
   :authentication/sign-in-success
   sign-in-success)

  (kyber-event-fx
   :authentication/sign-in-failure
   (sign-in-failure
    {:resend-verification-code
     (fn [email]
       #(re-frame/dispatch
         [:authentication/resend-verification-code {:email email}]))}))

  (kyber-event-fx
   :authentication/sign-in
   sign-in)

  (kyber-event-fx
   :authentication/sign-out
   sign-out)

  (kyber-event-fx
   :authentication/passwordless-sign-in
   passwordless-sign-in)

  (kyber-event-fx
   :authentication/passwordless-sign-in-success
   passwordless-sign-in-success)

  (kyber-event-fx
   :authentication/passwordless-sign-in-failure
   passwordless-sign-in-failure)

  (kyber-event-fx
   :authentication/passwordless-sign-in-reset
   passwordless-sign-in-reset)

  (kyber-event-fx
   :authentication/clear-cookies
   clear-cookies)

  (kyber-event-fx
   :authentication/clear-cookies-failure
   clear-cookies-failure)

  (kyber-event-fx
   :authentication/clear-cookies-success
   clear-cookies-success)

  (kyber-event-fx
   :authentication/resend-verification-code-success
   resend-verification-code-success)

  (kyber-event-fx
   :authentication/resend-verification-code-failure
   resend-verification-code-failure)

  (kyber-event-fx
   :authentication/resend-verification-code
   resend-verification-code)

  (kyber-event-fx
   :authentication/on-session-success-hook
   on-session-success-hook)

  (kyber-event-fx
   :authentication/follow-internal-redirect
   follow-internal-redirect)

  (kyber-event-fx
   :authentication/get-session-success
   get-session-success)

  (kyber-event-fx
   :authentication/get-session-failure
   get-session-failure)

  (kyber-event-fx
   :authentication/get-session
   get-session)

  (kyber-event-fx
   :authentication/redirect-to-idp
   redirect-to-idp)

  (kyber-event-fx
   :authentication/complete-session-initialization
   complete-session-initialization)

  (kyber-event-fx
   :authentication/get-user-attributes-success
   [update-application-context]
   get-user-attributes-success)

  (kyber-event-fx
   :authentication/get-user-attributes-failure
   get-user-attributes-failure)

  (kyber-event-fx
   :authentication/update-user-preferences-success
   update-user-preferences-success)

  (kyber-event-fx
   :authentication/update-user-preferences-failure
   update-user-preferences-failure)

  (kyber-event-fx
   :authentication/update-user-preferences
   update-user-preferences)

  (kyber-event-fx
   :authentication/update-user-password-success
   update-user-password-success)

  (kyber-event-fx
   :authentication/update-user-password-failure
   update-user-password-failure)

  (kyber-event-fx
   :authentication/update-user-password
   update-user-password)

  (kyber-event-fx
   :authentication/refresh-credentials-success
   refresh-credentials-success)

  (kyber-event-fx
   :authentication/refresh-credentials-failure
   refresh-credentials-failure)

  (kyber-event-fx
   :authentication/refresh-credentials
   refresh-credentials)

  (kyber-event-fx
   :authentication/forgot-password-success
   forgot-password-success)

  (kyber-event-fx
   :authentication/forgot-password-failure
   (forgot-password-failure
    {:resend-verification-code
     (fn [email]
       #(re-frame/dispatch
         [:authentication/resend-verification-code {:email email}]))}))

  (kyber-event-fx
   :authentication/forgot-password
   forgot-password)

  (kyber-event-fx
   :authentication/reset-password-success
   reset-password-success)

  (kyber-event-fx
   :authentication/reset-password-failure
   reset-password-failure)

  (kyber-event-fx
   :authentication/reset-password
   reset-password)

  (kyber-event-fx
   :authentication/refresh-tokens-success
   refresh-tokens-success)

  (kyber-event-fx
   :authentication/refresh-tokens-failure
   refresh-tokens-failure)

  (kyber-event-fx
   :authentication/authenticate-cognito-token-success
   authenticate-cognito-token-success)

  (kyber-event-fx
   :authentication/authenticate-cognito-token-failure
   authenticate-cognito-token-failure)

  (kyber-event-fx
   :authentication/authenticate-cognito-token
   authenticate-cognito-token)

  (kyber-event-fx
   :authentication/get-self-success
   get-self-success)

  (kyber-event-fx
   :authentication/get-self-failure
   get-self-failure)

  (kyber-event-fx
   :authentication/get-self
   get-self))
